import { Analytics } from "../../../utils/common/analytics";

export default class ProductPricingDataLayerAsync {
  constructor(data, zone, totalPPSections) {
    if (window.dataLayer && data && totalPPSections) {
      this.dataLayer = window.dataLayer;
      this.rawData = data.slice();
      this.zone = zone;
      this.totalPPSections = totalPPSections;
      this.dataLayer.storeType = zone ? zone.toLowerCase() + " - store" : null;
      this.dataLayer.pageType = "Product";
      this.dataLayer.pageSubType = "Product-Pricing";
      this.dataPPKeys = null;
      this.normalizedData = null;
      this.adobeNormalizedData = null;
      this.tabs = [].slice.call(document.querySelectorAll(".cmp-tabs__tab"));
      this.analytics = Analytics.getInstance();
      this.init();
    }
  }

  init() {
    this.dataLayer.items = [];
    if (this.tabs.length <= 0) {
      this.pageWithNoTabs();
    } else {
      this.pageWithTabs();
    }
  }

  pageWithNoTabs() {
    const { normalizedData, adobeNormalizedData } = this.normalizeData();

    this.normalizedData = normalizedData;
    this.adobeNormalizedData = adobeNormalizedData;

    // For pages with no tabs, add "NONE" for tabTitle.
    this.addTabTitles(this.normalizedData, "NONE");
    this.addTabTitles(this.adobeNormalizedData, "NONE");

    this.dataLayer.items = [...this.normalizedData];

    if (window._satellite) {
      window._satellite.track("Product View");
    }
    this.analytics.addEvent(Analytics.eventType.PRODUCT_VIEW, {
      items: [...this.adobeNormalizedData],
    });
  }

  normalizeData() {
    let normalizedData = [];
    let adobeNormalizedData = [];

    if (this.rawData && this.rawData.length) {
      for (let i = 0; i < this.rawData.length; i++) {
        if (this.rawData[i].products && this.rawData[i].products.length) {
          for (let j = 0; j < this.rawData[i].products.length; j++) {
            let rawProduct = this.rawData[i].products[j];
            let product = {};
            let adobeProduct = {};
            product["product-name"] = rawProduct.productTitle;
            product["product-sku"] =
              rawProduct.isFree && !rawProduct.dataLayerSku
                ? rawProduct.productTitle
                : rawProduct.dataLayerSku;
            product["product-price"] = this.formatPrice(
              rawProduct.displayPrice
            );
            product["product-currency"] = rawProduct.currency;
            normalizedData.push({ productInfo: product });

            adobeProduct["name"] = product["product-name"];
            adobeProduct["sku"] = product["product-sku"];
            adobeProduct["price"] = product["product-price"];
            adobeProduct["currency"] = product["product-currency"];
            adobeNormalizedData.push({ productInfo: adobeProduct });
          }
        }
      }
    }
    return { normalizedData, adobeNormalizedData };
  }

  addTabTitles(normalizedData, tabTitle) {
    for (const data of normalizedData) {
      data.tabTitle = tabTitle;
    }
    return normalizedData;
  }

  formatPrice(price) {
    if (price) {
      return price.replace(/[^0-9.-]+/g, "");
    } else {
      // Free or Contact Us products have undefined for price, change it to "0".
      return "0";
    }
  }

  allPPSectionsProcessed(hasTabs) {
    if (this.dataLayer.items) {
      if (hasTabs) {
        let ppSections = [].slice.call(
          document.querySelectorAll(
            ".cmp-tabs__tabpanel--active .product-pricing-section"
          )
        );
        return ppSections.length === this.dataLayer.items.length;
      } else {
        return this.totalPPSections === this.dataLayer.items.length;
      }
    }
  }

  pageWithTabs() {
    let activeTabPPKeys = this.getActiveTabPPKeys();
    let dataPPKeys = this.getDataPPKeys();
    if (this.activeTabHasPPSection(activeTabPPKeys, dataPPKeys)) {
      let tabTitle = [].slice.call(
        document.querySelectorAll(".cmp-tabs__tab--active")
      )[0].innerText;

      const { normalizedData, adobeNormalizedData } = this.normalizeData();

      this.normalizedData = normalizedData;
      this.adobeNormalizedData = adobeNormalizedData;
      this.addTabTitles(this.normalizedData, tabTitle);
      this.addTabTitles(this.adobeNormalizedData, tabTitle);

      this.dataLayer.items = [...this.normalizedData];
      if (window._satellite) {
        window._satellite.track("Product View");
      }
      this.analytics.addEvent(Analytics.eventType.PRODUCT_VIEW, {
        items: [...this.adobeNormalizedData],
      });
    }
  }

  getActiveTabPPKeys() {
    let activePPCards = [].slice.call(
      document.querySelectorAll(".cmp-tabs__tabpanel--active [data-cardid]")
    );
    let activeTabPPKeys = {};
    for (let i = 0; i < activePPCards.length; i++) {
      let key = activePPCards[i].getAttribute("data-cardid");
      activeTabPPKeys[key] = true;
    }
    return activeTabPPKeys;
  }

  getDataPPKeys() {
    if (this.dataPPKeys) {
      return this.dataPPKeys;
    }
    this.dataPPKeys = {};
    for (let i = 0; i < this.rawData.length; i++) {
      this.dataPPKeys[this.rawData[i].id] = true;
    }
    return this.dataPPKeys;
  }

  activeTabHasPPSection(activeTabPPKeys, dataPPKeys) {
    for (let key in dataPPKeys) {
      if (activeTabPPKeys[key]) {
        return true;
      }
    }
    return false;
  }

  getPanelPPKeys(panel) {
    let ppCards = [].slice.call(
      panel.querySelectorAll(".cmp-tabs__tabpanel [data-cardid]")
    );
    let panelPPKeys = {};
    for (let i = 0; i < ppCards.length; i++) {
      let key = ppCards[i].getAttribute("data-cardid");
      panelPPKeys[key] = true;
    }
    return panelPPKeys;
  }

  panelHasPPSection(panelPPKeys, dataPPKeys) {
    for (let key in dataPPKeys) {
      if (panelPPKeys[key]) {
        return true;
      }
    }
    return false;
  }

  removePrevDLItems() {
    let newDataLayer = [];
    if (this.dataLayer.items) {
      for (let i = 0; i < this.dataLayer.items.length; i++) {
        if (Array.isArray(this.dataLayer.items[i])) {
          newDataLayer.push(this.dataLayer.items[i]);
        }
      }
    }
    window.dataLayer.items = newDataLayer;
  }
}